// Main DataTable composition. Server-driven: parent owns data fetching,
// hands the (state, data, total, isLoading, isError) tuple in.
//
// Accessibility:
//   - <table role="table"> with semantic <thead>/<tbody>
//   - aria-sort on sortable columns
//   - aria-busy on tbody during loading
//   - aria-live region announces total + page changes
//   - All interactive elements are buttons or labelled inputs

import { useMemo } from "react";
import type { JSX } from "react";
import type { DataTableProps } from "./types.js";
import { DataTableHeader } from "./DataTableHeader.js";
import { DataTableBody } from "./DataTableBody.js";
import { DataTablePagination } from "./DataTablePagination.js";
import { DataTableFilters } from "./DataTableFilters.js";
import { DataTableToolbar } from "./DataTableToolbar.js";

const SHELL_CLS = "bg-white rounded-xl shadow-sm border border-slate-100 p-4";
const TABLE_CLS = "w-full text-left";

function announce(total: number, page: number, isLoading: boolean): string {
  if (isLoading) return "Loading data";
  return `${total} results, page ${page}`;
}

export function DataTable<T>(props: DataTableProps<T>): JSX.Element {
  const {
    state, columns, data, total, rowKey, ariaLabel,
    isLoading = false, isError = false, errorMessage, emptyState,
    pageSizeOptions, selectable = false, bulkActions,
    searchPlaceholder, enableDateRange = false, className,
    highlightId,
  } = props;

  const visibleRowIds = useMemo<readonly string[]>(
    () => data.map(rowKey),
    [data, rowKey],
  );

  // When a search term is active, swap the page-supplied "no data yet" copy
  // for a search-aware message. Without this, a coordinator typing into
  // search and getting zero hits sees "Add your first patient" — misleading
  // because data exists, the search just narrowed it to zero.
  const effectiveEmptyState =
    state.query.search && state.query.search.trim() !== ""
      ? "No results match your search."
      : emptyState;

  return (
    <div className={`${SHELL_CLS} ${className ?? ""}`}>
      <DataTableToolbar
        state={state}
        searchPlaceholder={searchPlaceholder}
        enableDateRange={enableDateRange}
        bulkActions={selectable ? bulkActions : undefined}
      />
      <div className="overflow-x-auto">
        <table className={TABLE_CLS} aria-label={ariaLabel}>
          <DataTableHeader
            columns={columns}
            state={state}
            selectable={selectable}
            visibleRowIds={visibleRowIds}
            filterRow={<DataTableFilters columns={columns} state={state} />}
          />
          <DataTableBody
            columns={columns}
            data={data}
            rowKey={rowKey}
            state={state}
            selectable={selectable}
            isLoading={isLoading}
            isError={isError}
            errorMessage={errorMessage}
            emptyState={effectiveEmptyState}
            highlightId={highlightId}
          />
        </table>
      </div>
      <DataTablePagination state={state} total={total} pageSizeOptions={pageSizeOptions} />
      <div role="status" aria-live="polite" className="sr-only">
        {announce(total, state.query.page, isLoading)}
      </div>
    </div>
  );
}
